/**
 * Home container component responsible for acting as a container for the homepage.
 * @namespace HomeContainer
 * @memberOf Home
 */
import React, { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useKindeAuth } from "@kinde-oss/kinde-auth-react"
import { useUserStore } from "stores/userStore"

import ProjectDisplay from "features/Projects/components/ProjectDisplay.component"

import {
  useProjectsQuery,
  useArchivedProjectsQuery,
  useProjectFoldersQuery,
} from "features/Projects/api/useProjectsQuery"
import {
  useArchiveProjectMutation,
  useUnarchiveProjectMutation,
} from "features/Projects/api/useProjectsMutations"
import { useUser } from "hooks/api/useUser"
import { PROJECT_FILTER } from "types/project.types"
import { KindeRole, KindeRolesClaim } from "types/kinde.types"
import { hasAdminPrivileges } from "types/userRole.types"
import {
  ProjectView,
  isRevealFolderState,
  loadListState,
  normaliseView,
  saveListState,
} from "features/Projects/utils/projectListState"
import { folderAncestors } from "utils/projectTree"

// The project list each live view asks for.
const FILTER_FOR_VIEW: Record<Exclude<ProjectView, "archived">, PROJECT_FILTER> =
  {
    owned: PROJECT_FILTER.OWNED,
    member: PROJECT_FILTER.MEMBER,
    all: PROJECT_FILTER.ALL,
  }

const HomeContainer: React.FC = () => {
  const { getUserData } = useUser()
  const user = useUserStore((state) => state.user)

  const { getClaim } = useKindeAuth()
  // null until the roles claim has been read.
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null)

  const location = useLocation()
  const navigate = useNavigate()

  // The remembered view (Owned / Member / All / Archived), saved per browser.
  // Opened from a project, the project's view from the start, so the list
  // doesn't load the remembered one first.
  const [storedView, setStoredView] = useState<ProjectView>(() => {
    const state: unknown = location.state
    return isRevealFolderState(state) ? state.view : loadListState().view
  })
  // Opened in "All" from a project: the project's own view, in case "All"
  // turns out not to be open to the user.
  const [revealOwnView, setRevealOwnView] = useState<ProjectView | null>(
    () => {
      const state: unknown = location.state
      return isRevealFolderState(state) ? state.ownView ?? null : null
    }
  )
  // The archived list is only fetched once the user opens the archived view.
  const [archivedRequested, setArchivedRequested] = useState<boolean>(
    () => storedView === "archived"
  )

  // The open folders, saved per browser with the view.
  const [openFolders, setOpenFolders] = useState<string[]>(
    () => loadListState().openFolders
  )
  // A folder to scroll to and highlight once the list shows it.
  const [revealFolder, setRevealFolder] = useState<string | null>(null)

  const userReady = Boolean(user.username && user.unit)

  useEffect(() => {
    const loadUserRoles = async () => {
      try {
        const userRoles = (await getClaim("roles")) as KindeRolesClaim | null
        setIsAdmin(
          userRoles?.value?.some((role: KindeRole) =>
            hasAdminPrivileges(role.key)
          ) || false
        )
      } catch (error) {
        console.error("Failed to load user roles:", error)
        setIsAdmin(false)
      }
    }
    loadUserRoles()
  }, [getClaim])

  // A remembered "All" waits for the roles claim, so a non-admin never asks
  // for every project and an admin doesn't load Owned first.
  const waitingForRole = storedView === "all" && isAdmin === null
  const view = waitingForRole
    ? storedView
    : storedView === "all" && isAdmin === false && revealOwnView
      ? revealOwnView
      : normaliseView(storedView, isAdmin === true)
  const filter =
    view === "archived" ? PROJECT_FILTER.OWNED : FILTER_FOR_VIEW[view]

  useEffect(() => {
    if (!user.username || !user.unit) {
      getUserData()
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const projectsQuery = useProjectsQuery(
    filter,
    userReady && !waitingForRole && view !== "archived"
  )
  const foldersQuery = useProjectFoldersQuery(userReady)
  const archivedQuery = useArchivedProjectsQuery(userReady && archivedRequested)

  const archiveMutation = useArchiveProjectMutation()
  const unarchiveMutation = useUnarchiveProjectMutation()

  const showView = (next: ProjectView): void => {
    setStoredView(next)
    if (next !== "archived") return
    // Opening the archived view refreshes it, as the old archive switch did.
    if (archivedRequested) {
      void archivedQuery.refetch()
    } else {
      setArchivedRequested(true)
    }
  }

  const changeView = (next: ProjectView): void => {
    // A reveal still waiting belongs to the view the user is leaving.
    setRevealFolder(null)
    showView(next)
    saveListState({ ...loadListState(), view: next })
  }

  const changeOpenFolders = (next: string[]): void => {
    setOpenFolders(next)
    saveListState({ ...loadListState(), openFolders: next })
  }

  // Opened from a project (breadcrumb or "All projects"): show its view and
  // hand the folder to the table, which opens it and its parents (under the
  // tree's own spelling) and points at it once the list is loaded. The router
  // state is cleared so Back or a reload doesn't do it again.
  useEffect(() => {
    const state: unknown = location.state
    if (!isRevealFolderState(state)) return
    // On arrival the view already starts as the project's view.
    if (state.view !== storedView) showView(state.view)
    setRevealOwnView(state.ownView ?? null)
    // "All" is only asked for when the list was left on All: that stays
    // remembered, whichever view ends up showing.
    if (state.view !== "all") {
      saveListState({ ...loadListState(), view: state.view })
    }
    // "Demo..Trackers" or stray spaces still name Demo.Trackers.
    const ancestors = folderAncestors(state.revealFolder)
    setRevealFolder(ancestors[ancestors.length - 1] ?? null)
    navigate(location.pathname, { replace: true, state: null })
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state])

  const handleArchive = async (id: string, owner: string): Promise<void> => {
    // Errors are notified in the mutation's onError; swallow the rejection so
    // awaiting callers just resume.
    await archiveMutation.mutateAsync({ id, owner }).catch(() => undefined)
  }

  const handleUnarchive = async (id: string, owner: string): Promise<void> => {
    await unarchiveMutation.mutateAsync({ id, owner }).catch(() => undefined)
  }

  return (
    <div id="home" className="flex flex-col">
      <ProjectDisplay
        view={view}
        onViewChange={changeView}
        isAdmin={isAdmin === true || waitingForRole}
        canManageAll={isAdmin === true}
        // Loading until the list has been asked for, so the table can tell
        // an empty list from one not fetched yet.
        isLoading={!userReady || projectsQuery.isFetching || waitingForRole}
        isLoadingArchived={!userReady || archivedQuery.isFetching}
        projects={{ result: projectsQuery.data ?? [] }}
        archivedProjects={{ result: archivedQuery.data ?? [] }}
        folders={foldersQuery.data ?? []}
        openFolders={openFolders}
        onOpenFoldersChange={changeOpenFolders}
        revealFolder={revealFolder}
        onRevealDone={() => setRevealFolder(null)}
        handleArchive={handleArchive}
        handleUnarchive={handleUnarchive}
      />
    </div>
  )
}

export default HomeContainer
